import { ReportGroup } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { Box, Chip, CircularProgress, Tooltip, Typography, styled } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTenants } from '@/services/tenants/useTenants';
import { useUpdateReportGroupName } from '@/hooks/api/reports/useUpdateReportGroupName';
import { useEffect, useRef, useState } from 'react';
import { useToast } from '@/hooks/useToast';
import { pluralize } from '@/utils/format';
import { TreeBranch, CompactGroupHeight, GroupSampleHeight } from './ReportSample.styles';
import { GroupThumbnails } from './GroupThumbnails';
import { GroupNameEditor } from './GroupNameEditor';
import { GroupNameDisplay } from './GroupNameDisplay';
import { CardWrapper } from './CardWrapper';
import { DeleteGroupModal } from '@/components/modals/DeleteGroupModal';
import { useReportMasterDetail } from '../ReportMasterDetailContext';
import { useInfiniteReportGroupAssets } from '@/hooks/api/reports/useInfiniteReportGroupAssets';
import { Thumbnail } from '@/components/Thumbnail';
import { getAssetFileName } from '@/utils/assets';

const AssetListItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 6px',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ExpandedAssetList = ({ groupId }: { groupId: string }) => {
  const { id: reportId } = useParams();
  const sentinelReference = useRef<HTMLDivElement>(null);
  const scrollContainerReference = useRef<HTMLDivElement>(null);

  const { assets, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteReportGroupAssets({
    reportId,
    groupId,
    enabled: true,
  });

  const assetList: typeof assets = [];
  const seen = new Set<string>();
  for (const asset of assets) {
    if (!seen.has(asset.id)) {
      seen.add(asset.id);
      assetList.push(asset);
    }
  }

  useEffect(() => {
    const sentinel = sentinelReference.current;
    const scrollContainer = scrollContainerReference.current;
    if (!sentinel || !scrollContainer) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: scrollContainer, rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <Box
      ref={scrollContainerReference}
      onClick={event => event.stopPropagation()}
      sx={{ mt: 0.5, maxHeight: 300, overflowY: 'auto' }}
    >
      {assetList.map(asset => (
        <AssetListItem key={asset.id}>
          <Box sx={{ flexShrink: 0 }}>
            <Thumbnail asset={asset} size={28} noLink />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title={asset.path} enterDelay={500}>
              <Typography variant='body2' noWrap sx={{ fontSize: '0.75rem', color: 'text.primary' }}>
                {getAssetFileName(asset)}
              </Typography>
            </Tooltip>
          </Box>
          <Chip
            label={asset.matches?.length ?? 0}
            size='small'
            sx={{
              flexShrink: 0,
              minWidth: 22,
              height: 18,
              backgroundColor: 'action.selected',
              color: 'text.secondary',
              '& .MuiChip-label': { px: 0.5, fontSize: '0.6875rem' },
            }}
          />
        </AssetListItem>
      ))}
      <div ref={sentinelReference} />
    </Box>
  );
};

export const ReportSample = ({
  group,
  groupIndex,
  isSelected,
  isLastItem,
}: {
  group: ReportGroup;
  groupIndex: number;
  isSelected: boolean;
  isLastItem?: boolean;
}) => {
  const { id } = useParams();
  const { getPath } = useTenants();
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(group.name || `Group ${groupIndex}`);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contextMenuAnchor, setContextMenuAnchor] = useState<{ mouseX: number; mouseY: number } | undefined>();
  const showToast = useToast(state => state.showToast);
  const { hasMatchData } = useReportMasterDetail();

  const { mutate: updateGroupName, isPending: isUpdatingGroupName } = useUpdateReportGroupName({
    onSuccess: () => {
      showToast({ message: 'Group name updated successfully', type: 'success' });
    },
    onError: error => {
      showToast({ message: error, type: 'error' });
    },
  });

  const groupName = group.name || `Group ${groupIndex}`;

  const handleSave = (event?: React.MouseEvent<HTMLButtonElement | HTMLInputElement>) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (id && group.id) {
      updateGroupName({
        reportId: id,
        groupId: group.id,
        name: editedName.trim(),
      });
      setIsEditing(false);
    }
  };

  const handleCancel = (event?: React.MouseEvent<HTMLButtonElement | HTMLInputElement>) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setEditedName(groupName);
    setIsEditing(false);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSave();
    } else if (event.key === 'Escape') {
      handleCancel();
    }
  };

  const handleEdit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsEditing(true);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuAnchor({ mouseX: event.clientX, mouseY: event.clientY });
  };

  const handleContextMenuClose = () => {
    setContextMenuAnchor(undefined);
  };

  const showExpandedAssets = isSelected && hasMatchData;

  return (
    <Box
      display='flex'
      alignItems='stretch'
      minHeight={showExpandedAssets ? CompactGroupHeight : GroupSampleHeight}
      sx={{ minWidth: 0, overflow: 'visible' }}
    >
      <TreeBranch isLastItem={isLastItem} compact={showExpandedAssets} />
      <CardWrapper
        isSelected={isSelected}
        expanded={showExpandedAssets}
        isEditing={isEditing}
        to={getPath(`report/${id}/${group.id}`)}
        onContextMenu={handleContextMenu}
      >
        {isEditing || isUpdatingGroupName ? (
          <GroupNameEditor
            editedName={editedName}
            setEditedName={setEditedName}
            handleSave={handleSave}
            isUpdatingGroupName={isUpdatingGroupName}
            handleCancel={handleCancel}
            handleKeyPress={handleKeyPress}
          />
        ) : (
          <GroupNameDisplay
            groupName={groupName}
            onEdit={handleEdit}
            onDelete={handleDelete}
            contextMenuAnchor={contextMenuAnchor}
            onContextMenuClose={handleContextMenuClose}
          />
        )}
        <BodyText marginBottom={showExpandedAssets ? '0' : '4px'}>
          {pluralize(group.assetIds.length, 'asset', 'assets')}
        </BodyText>
        {showExpandedAssets ? <ExpandedAssetList groupId={group.id} /> : <GroupThumbnails group={group} />}
      </CardWrapper>
      {id && (
        <DeleteGroupModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          group={group}
          groupName={groupName}
          reportId={id}
        />
      )}
    </Box>
  );
};
